import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService } from 'ui-components/services/notification-service';

import { failedToUpdateUserNotification } from './constants/notifications';
import { messages } from './messages';
import { ReusableGridButton } from '../../../components/grid-header/reusable-grid-button';
import Global from '../../../store/global-selectors';
import { updateUsersList } from '../../../store/user/user.actions';
import { updateUser, getSellerUsers } from '../../../store/user/user.api';

/**
 * Renders either a deactivate or reactivate button based on the user's current status.
 * For the current user, it displays a message instead of a button.
 * For other users, it shows a button that allows administrators to toggle the user's active status.
 * @param {object} props - The component props
 * @param {object} props.user - The user object containing details about the user to be activated/deactivated
 * @param {(users: object[]) => void} props.setUsers - Function to update the users array state in the parent component
 * @param {string} [props.deactivateTheme] - Theme to apply to the deactivate button
 * @param {string} [props.activateTheme] - Theme to apply to the activate button
 * @param {string} [props.className] - Additional CSS class name to apply to the button
 * @returns {React.ReactElement} A button to activate/deactivate the user or a message for the current user
 */
export const DeactivateUserButtonValue = ({
  user,
  setUsers,
  deactivateTheme = THEME_CONSTANTS['danger-link'],
  activateTheme = THEME_CONSTANTS.link,
  className,
}) => {
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const isCurrentUserAnAdmin = useSelector(Global.selectUserhasAdministrationRole);

  const dispatch = useDispatch();
  const { userId, email, isWorkstationApprovalRequired, name, restrictions, roles, username } = user;

  const updateUserActiveStatus = useCallback(
    (shouldDeactivateUser) => {
      const payload = {
        email,
        isActive: !shouldDeactivateUser,
        isWorkstationApprovalRequired,
        name,
        password: '',
        restrictions,
        roles,
        userId,
        username,
      };

      const updateUserPromise = updateUser(userId, payload);
      updateUserPromise
        .then((resp) => {
          setUsers((prevUsers) => prevUsers.map((u) => (u.userId === resp.data.userId ? { ...u, isActive: !shouldDeactivateUser } : u)));
          getSellerUsers()
            .then((response) => {
              dispatch(updateUsersList(response.data));
            })
            .catch();
        })
        .catch(() => {
          NotificationService.showNotification(failedToUpdateUserNotification);
        });
    },
    [dispatch, email, isWorkstationApprovalRequired, name, restrictions, roles, setUsers, userId, username],
  );

  const onClickDeactivate = useCallback(() => {
    updateUserActiveStatus(true);
  }, [updateUserActiveStatus]);

  const onClickReactivate = useCallback(() => {
    updateUserActiveStatus(false);
  }, [updateUserActiveStatus]);

  if (user.userId === currentUser.userId) {
    return <FormattedMessage {...messages.currentUser} />;
  }

  if (!user.isActive) {
    return (
      <ReusableGridButton
        actionHandler={onClickReactivate}
        className={className}
        disable={!isCurrentUserAnAdmin}
        hasTooltip={!isCurrentUserAnAdmin}
        messageDescriptor={messages.reactivate}
        theme={activateTheme}
        tooltipMessageDescriptor={messages.contactAdministrator}
      />
    );
  }
  return (
    <ReusableGridButton
      actionHandler={onClickDeactivate}
      className={className}
      disable={!isCurrentUserAnAdmin}
      hasTooltip={!isCurrentUserAnAdmin}
      messageDescriptor={messages.deactivate}
      theme={deactivateTheme}
      tooltipMessageDescriptor={messages.contactAdministrator}
    />
  );
};
